import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { useFormatters } from '../../contexts/PreferencesContext'
import type { DrilldownHeadline } from '../../contexts/DrilldownContext'
import { formatChartPercent, otdHeadlineFromPoint, otifHeadlineFromPoint } from './chartHeadline'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts'
import { SupplierRankingCard } from './SupplierRankingCard'
import { SHOW_SUPPLIERS_REQUIRING_ATTENTION } from './dashboardSectionVisibility'
import { DrillDownModal, type DrillDownData } from './DrillDownModal'
import { TabProps, RechartsClickData } from './types'
import { useDrilldownAction, OTIFDrilldownModal, OTDDrilldownModal } from '../drilldown'
import { ChartWrapper } from './ChartWrapper'
import { milestoneLines, milestoneMargin, milestoneMarks } from './milestoneMarkers'
import { ArrearsBacklogChart } from './charts/ArrearsBacklogChart'
import { useAxisAmount } from './charts/useAxisAmount'
import { arrearsBacklogDrillFilters, type ArrearsBacklogBar } from './arrearsBacklogDrill'
import { SectionTourTrigger } from '../tour/SectionTourTrigger'
import { useAuth } from '../../contexts/AuthContext'
import { useMenuSettings } from '../../contexts/MenuSettingsContext'
import { usePortalLabels } from '../../lib/portalLabels'
import { useQualityMeasureLabel } from '../../lib/qualityLabels'
import { KPIMetricsSection } from './KPIMetricsSection'
import { ScheduleCollaborationSection } from './ScheduleCollaborationSection'
import { calculateTrendLine, getPercentageDomain, parseMonthToDateRange } from './dashboardUtils'
import { buildOtifDrillFilters, otifMonthBucketFilters, formatPeriodLabel, defaultTabDrilldownFilters } from './dashboardFilterUtils'
import { OTDChartTooltip, OTIFChartTooltip, PPMChartTooltip } from './DefaultTabTooltips'
import { LIVE_NCRS_OVERDUE_METRIC, liveNcrsBarClick, liveNcrsDrillFilters, type NcrLifecycle } from './liveNcrsDrill'
import { showsOtd, showsOtif } from './deliveryMetricMode'

export function DefaultTab({ data, currency, filters, dataPeriod: _dataPeriod, orderType }: TabProps) {
  const { formatCurrency, preferences } = useFormatters()
  // CR-7120: graph axes show plain numbers; tooltips and cards keep the symbol.
  const axisAmount = useAxisAmount()
  const { user } = useAuth()
  const { isFeatureEnabled } = useMenuSettings()
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration')
  const portalLabels = usePortalLabels(user?.platform)
  const qualityLabel = useQualityMeasureLabel()
  // Chart series keyed by the backend `ppm` metric field use the
  // configured short label ('PPM' / 'PPK') when shown to the user.
  const metricDisplayNames = useMemo<Record<string, string>>(
    () => ({ ppm: qualityLabel, ncr: 'NCR', otif: 'OTIF', otd: 'OTD' }),
    [qualityLabel],
  )

  // CR-4641 round 2: chart visibility is the AND of the company-wide
  // delivery_metric_mode admin override (CR-8697: incl. 'none') AND whether
  // the metric has data after the SupplierProfile flag filter (backend sends
  // null when the filtered queryset is empty).
  const showOTIF = showsOtif(data.delivery_metric_mode) && data.quality.otif !== null
  const showOTD = showsOtd(data.delivery_metric_mode) && data.delivery.otd !== null
  const showPPM = data.quality.ppm !== null

  // CR-7776 / CR-7751: every dashboard filter, under the tile's names
  // (dashboardFilterUtils), for every drill on this tab except OTIF.
  const drilldownFilters = defaultTabDrilldownFilters(filters, orderType)

  // CR-6405: the OTIF drill's scope — the same builder the Site KPI and Group
  // Detail tabs call, so a filter that narrows this tab's OTIF ring narrows the
  // drill opened from it. Not the object above: it adds the panel's Division
  // (as `group`, which the OTIF drill scopes on).
  const otifDrillFilters = buildOtifDrillFilters(filters, { orderType })

  const periodLabel = formatPeriodLabel(filters?.dateFrom, filters?.dateTo)

  // Calculate data with trend lines
  const spendDataWithTrend = calculateTrendLine(data.charts.spendByMonth || [], 'spend', 'trend')
  const otifDataWithTrend = calculateTrendLine(data.charts.otifData || [], 'otif', 'otifTrend')
  const otdDataWithTrend = calculateTrendLine(data.charts.otdData || [], 'otd', 'otdTrend')
  const ppmDataWithTrend = calculateTrendLine(data.charts.ppmTrend || [], 'ppm', 'ppmTrend')
  // CR-5880: the tenant's milestones, as dashed lines (the PPM graph is future scope).
  const otifMarks = milestoneMarks(data.charts.milestones, 'otif', otifDataWithTrend.map((d) => String(d['month'])))
  const otdMarks = milestoneMarks(data.charts.milestones, 'otd', otdDataWithTrend.map((d) => String(d['month'])))

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  // Modal state for supplier drill-down
  const [drillDownData, setDrillDownData] = useState<DrillDownData | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // OTIF specialized drill-down modal state
  const [isOTIFModalOpen, setIsOTIFModalOpen] = useState(false)
  const [otifModalContext, setOtifModalContext] = useState<string | undefined>(undefined)
  const [otifModalInitialTab, setOtifModalInitialTab] = useState<'failed' | 'arrears' | 'positive' | 'total' | undefined>(undefined)
  const [otifModalFilters, setOtifModalFilters] = useState<Record<string, string>>(otifDrillFilters)

  // OTD specialized drill-down modal state
  const [isOTDModalOpen, setIsOTDModalOpen] = useState(false)
  const [otdModalContext, setOtdModalContext] = useState<string | undefined>(undefined)
  const [otdModalInitialTab, setOtdModalInitialTab] = useState<'on_time' | 'early' | 'late' | 'total' | undefined>(undefined)
  // CR-7261: the clicked month's figure, carried into the OTD drill-down header.
  const [otifModalHeadline, setOtifModalHeadline] = useState<DrilldownHeadline | undefined>(undefined)
  const [otdModalHeadline, setOtdModalHeadline] = useState<DrilldownHeadline | undefined>(undefined)
  const [otdModalFilters, setOtdModalFilters] = useState<Record<string, string>>(drilldownFilters)

  // CR-3827: State for hover-to-highlight on chart legends
  const [hoveredOtifSeries, setHoveredOtifSeries] = useState<string | null>(null)
  const [hoveredOtdSeries, setHoveredOtdSeries] = useState<string | null>(null)
  const [hoveredPpmSeries, setHoveredPpmSeries] = useState<string | null>(null)
  const [hoveredSpendSeries, setHoveredSpendSeries] = useState<string | null>(null)

  // Format functions for supplier rankings
  const formatPPM = (value: number) => Math.round(value).toLocaleString()
  const formatPercentage = (value: number) => formatChartPercent(value, preferences.kpi_percent_decimals ?? 0)
  const formatCurrencyValue = (value: number) => formatCurrency(value)

  // Custom tooltip formatter for currency values (portal-aware)
  const currencyTooltipFormatter = (value: number, name: string) => {
    const spendLabel = user?.platform === 'SUPPLIER' ? 'Revenue' : 'Spend'
    const arrearsLabel = user?.platform === 'SUPPLIER' ? 'Backlog Value' : 'Arrears Value'
    return [formatCurrency(value), name === 'spend' ? spendLabel : name === 'value' ? arrearsLabel : name]
  }

  // Calculate domains for OTIF and OTD charts
  const otifDomain = getPercentageDomain(otifDataWithTrend, 'otif')
  const otdDomain = getPercentageDomain(otdDataWithTrend, 'otd')

  // Handle supplier click - open drill-down modal
  const handleSupplierClick = (supplierId: number, metricType: 'ppm' | 'otif' | 'otd' | 'arrears') => {
    let rankings
    let target

    switch (metricType) {
      case 'ppm':
        rankings = data.supplierRankings?.worstPPM || []; target = data.targets?.ppm; break
      case 'otif':
        rankings = data.supplierRankings?.worstOTIF || []; target = data.targets?.otif; break
      case 'otd':
        rankings = data.supplierRankings?.worstOTD || []; target = data.targets?.otd; break
      case 'arrears':
        rankings = data.supplierRankings?.highestArrears || []; target = undefined; break
    }

    const supplierData = rankings.find(s => s.supplier_id === supplierId)

    if (supplierData) {
      setDrillDownData({
        supplier_id: supplierData.supplier_id,
        supplier_name: supplierData.supplier_name,
        metric_type: metricType,
        current_value: supplierData.value,
        previous_value: supplierData.trend === 'stable' ? supplierData.value : undefined,
        // CR-7714 FE-20: the ranking row's target, else the configured one;
        // never 1500 / 88 / 90, and a real 0 target is kept.
        target: supplierData.target ?? target ?? undefined,
        details: undefined,
        summary: { total_pos: undefined, total_lines: undefined, total_value: undefined, affected_parts: undefined }
      })
      setIsModalOpen(true)
    }
  }

  // Handle chart click - open drill-down modal with month-specific data using API
  const handleChartClick = (chartData: RechartsClickData | null, metricType: 'ppm' | 'otif' | 'otd' | 'spend' | 'arrears' | 'ncrs', segment?: NcrLifecycle) => {
    if (!chartData) return

    let month: string | null = null
    if (chartData.activePayload && chartData.activePayload[0]) {
      month = (chartData.activePayload[0].payload?.['month'] as string) || null
    } else if (chartData.activeLabel) {
      month = chartData.activeLabel
    }
    if (!month) return

    const dateRange = parseMonthToDateRange(month)
    if (!dateRange) return

    const chartFilters = { ...drilldownFilters, date_from: dateRange.date_from, date_to: dateRange.date_to }

    // CR-4785: PPM clicks route through the shared openDrilldown flow, same
    // as the top-of-dashboard PPM KPI (which goes through DrilldownTrigger).
    // Previously this branch opened a stub PPMDrilldownModal that just said
    // "coming soon" — two entry points with divergent behaviour. Falling
    // through to the shared metricKeyMap handling below keeps future
    // changes (columns, filters, layout) applied to both entry points.

    // For OTIF, use the specialized OTIF drill-down modal
    if (metricType === 'otif') {
      let initialTab: 'failed' | 'arrears' | 'positive' | 'total' | undefined = undefined
      if (chartData.activePayload && chartData.activePayload.length > 0) {
        for (const item of chartData.activePayload) {
          if (item.dataKey === 'failed') { initialTab = 'failed'; break }
          else if (item.dataKey === 'arrears') { initialTab = 'arrears'; break }
          else if (item.dataKey === 'otifCount') { initialTab = 'positive'; break }
        }
      }
      // CR-7261: the clicked point carries otif + target; the header shows them.
      // Prefer the click's own payload and fall back to the month's point in
      // the chart data, exactly as the OTD branch below does — a click that
      // arrives with only a label still carries the figure.
      // `??` is NOT enough: clicking the Arrears or OTIF-PO-Lines BAR delivers a
      // payload that EXISTS and simply has no `otif` key, so a nullish fallback
      // never fires and the header silently loses its figure. Fall back on the
      // absence of the FIGURE, not the absence of the object.
      const otifClicked = chartData.activePayload?.[0]?.payload as Record<string, unknown> | undefined
      const otifPoint = (otifClicked && otifClicked['otif'] != null)
        ? otifClicked
        : (otifDataWithTrend.find(d => d.month === month) as Record<string, unknown> | undefined)
      setOtifModalHeadline(otifHeadlineFromPoint(otifPoint, preferences.kpi_percent_decimals ?? 0))
      // W3-T5 (amended 5 Sep 2026, FIX-01): the bar is a month bucket — in Live
      // its arrears are the overdue lines DUE that month, so the drill says so.
      // CR-6405: that rule is declared once, in otifMonthBucketFilters.
      setOtifModalFilters(otifMonthBucketFilters(otifDrillFilters, dateRange)); setOtifModalContext(month); setOtifModalInitialTab(initialTab); setIsOTIFModalOpen(true); return
    }

    // For OTD, use the specialized OTD drill-down modal
    if (metricType === 'otd') {
      let initialTab: 'on_time' | 'early' | 'late' | 'total' | undefined = undefined
      if (chartData.activePayload && chartData.activePayload.length > 0) {
        for (const item of chartData.activePayload) {
          if (item.dataKey === 'onTime') { initialTab = 'on_time'; break }
          else if (item.dataKey === 'early') { initialTab = 'early'; break }
          else if (item.dataKey === 'late') { initialTab = 'late'; break }
        }
      }
      // CR-7261: the clicked point carries otd + target; the header shows them.
      // Prefer the click's own payload; fall back to the month's point in the
      // chart data (the same lookup the NCR branch below already does), so a
      // click that arrives with only a label still carries the figure.
      const otdPoint = (chartData.activePayload?.[0]?.payload as Record<string, unknown> | undefined)
        ?? (otdDataWithTrend.find(d => d.month === month) as Record<string, unknown> | undefined)
      setOtdModalHeadline(otdHeadlineFromPoint(otdPoint, preferences.kpi_percent_decimals ?? 0))
      setOtdModalFilters(chartFilters); setOtdModalContext(month); setOtdModalInitialTab(initialTab); setIsOTDModalOpen(true); return
    }

    const metricKeyMap: Record<string, string> = { 'ppm': 'ppm', 'spend': 'spend', 'arrears': 'arrears', 'ncrs': LIVE_NCRS_OVERDUE_METRIC }
    const metricKey = metricKeyMap[metricType]
    if (!metricKey) return
    // CR-6676: the Live NCRs Overdue bar opens its own drill-down (ncr_date window,
    // same overdue rule) on the segment clicked (red/amber) — see liveNcrsDrill.ts.
    if (metricType === 'ncrs') {
      openDrilldown(metricKey, liveNcrsDrillFilters(chartFilters, data.charts.liveNCRsOverdue, month, segment), month)
      return
    }
    openDrilldown(metricKey, chartFilters, month)
  }

  // Arrears backlog bar click: the drill lists exactly the bar's own due-date window (arrearsBacklogDrill.ts)
  // CR-7714 BL-15: overdue lines with no value (no converted price, or no
  // rate on a mixed scope) are counted but not valued; say how many.
  const arrearsValueMissing = (data.charts.arrearsBacklog ?? []).reduce((acc, d) => acc + (d.value_missing ?? 0), 0)
  const handleArrearsBacklogClick = (barData: ArrearsBacklogBar) => {
    const chartFilters = arrearsBacklogDrillFilters(barData, drilldownFilters)
    if (chartFilters) openDrilldown('arrears', chartFilters, `Week ${barData.week}`)
  }

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4"></div>
        <SectionTourTrigger pageKey="dashboard" sectionKey="default" variant="link" label="Tour this tab" />
      </div>

      {/* KPI Metrics Section */}
      <KPIMetricsSection
        data={data}
        currency={currency}
        drilldownFilters={drilldownFilters}
        periodLabel={periodLabel}
        // CR-7261: the KPI card is not a chart point, so it carries no figure —
        // clear any headline a previous CHART click left behind, or the modal opens
        // headed "Last 12 months · chart 88.42%", a month's number over a year's rows.
        onOTIFClick={() => { setOtifModalHeadline(undefined); setOtifModalFilters(otifDrillFilters); setOtifModalContext(periodLabel); setOtifModalInitialTab('total'); setIsOTIFModalOpen(true) }}
        onOTDClick={() => { setOtdModalFilters(drilldownFilters); setOtdModalContext(periodLabel); setOtdModalInitialTab('total'); setIsOTDModalOpen(true) }}
      />

      {/* Supplier Rankings - Actionable Insights. CR-8235: hidden pending a
          product decision (see dashboardSectionVisibility). */}
      {SHOW_SUPPLIERS_REQUIRING_ATTENTION && data.supplierRankings && (
        <div data-tour="supplier-rankings">
          <h2 className="text-lg font-semibold mb-3 px-1">{portalLabels.suppliersRequiringAttention}</h2>
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${2 + (showOTIF ? 1 : 0) + (showOTD ? 1 : 0)} gap-4`}>
            <SupplierRankingCard title={portalLabels.worstQuality} suppliers={data.supplierRankings.worstPPM || []} metricType="ppm" formatValue={formatPPM} inverse={true} onSupplierClick={(id) => handleSupplierClick(id, 'ppm')} />
            {showOTIF && <SupplierRankingCard title={portalLabels.worstOTIF} suppliers={data.supplierRankings.worstOTIF || []} metricType="otif" formatValue={formatPercentage} inverse={false} onSupplierClick={(id) => handleSupplierClick(id, 'otif')} />}
            {showOTD && <SupplierRankingCard title={portalLabels.worstOTD} suppliers={data.supplierRankings.worstOTD || []} metricType="otd" formatValue={formatPercentage} inverse={false} onSupplierClick={(id) => handleSupplierClick(id, 'otd')} />}
            <SupplierRankingCard title={portalLabels.highestArrears} suppliers={data.supplierRankings.highestArrears || []} metricType="arrears" formatValue={formatCurrencyValue} inverse={true} onSupplierClick={(id) => handleSupplierClick(id, 'arrears')} />
          </div>
        </div>
      )}

      {/* First Row Charts — CR-4641 round 2: hide row entirely when both
          PPM and OTIF are excluded; show one column if only one is visible. */}
      {(showPPM || showOTIF) && (
      <div className={`grid gap-4 ${showPPM && showOTIF ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {/* PPM Chart */}
        {showPPM && <div data-tour="ppm-chart">
        <ChartWrapper title={qualityLabel} data={ppmDataWithTrend} exportFilename="ppm-trend"
          legend={[{ color: '#3b82f6', label: metricDisplayNames['ppm']!, dataKey: 'ppm' }, { color: '#ef4444', label: 'Target', dataKey: 'target' }, { color: '#f97316', label: 'Trend', dataKey: 'ppmTrend' }]}
          hoveredSeries={hoveredPpmSeries} onSeriesHover={setHoveredPpmSeries}>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={ppmDataWithTrend} onClick={(chartData) => handleChartClick(chartData, 'ppm')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              {/* CR-4537: interval={0} forces every month tick to render (recharts default culls end labels on long ranges e.g. Jan 23 – Oct 25); angle + smaller font keep them readable when dense */}
              {/* This chart sits in a half-width column, so twelve "Mon YY" ticks
                  already collide at 1440px; rotate from 7 points, not 13. */}
              <XAxis dataKey="month" tick={{ fontSize: ppmDataWithTrend.length > 24 ? 9 : 11 }} interval={0} angle={ppmDataWithTrend.length > 6 ? -45 : 0} textAnchor={ppmDataWithTrend.length > 6 ? 'end' : 'middle'} height={ppmDataWithTrend.length > 6 ? 55 : 30} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} content={<PPMChartTooltip measureLabel={qualityLabel} />} />
              <Line type="monotone" dataKey="ppm" stroke="#3b82f6" strokeWidth={hoveredPpmSeries === 'ppm' ? 3 : 2} strokeOpacity={hoveredPpmSeries && hoveredPpmSeries !== 'ppm' ? 0.2 : 1} dot={{ r: hoveredPpmSeries === 'ppm' ? 4 : 3 }} />
              <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={hoveredPpmSeries === 'target' ? 3 : 2} strokeOpacity={hoveredPpmSeries && hoveredPpmSeries !== 'target' ? 0.2 : 1} strokeDasharray="5 5" dot={false} />
              <Line type="monotone" dataKey="ppmTrend" stroke="#f97316" strokeWidth={hoveredPpmSeries === 'ppmTrend' ? 3 : 2} strokeOpacity={hoveredPpmSeries && hoveredPpmSeries !== 'ppmTrend' ? 0.2 : 1} strokeDasharray="5 5" dot={false} name="Trend" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>
        </div>}

        {/* OTIF Chart */}
        {showOTIF && <div data-tour="otif-chart">
        <ChartWrapper title="OTIF" data={otifDataWithTrend} exportFilename="otif-trend" kpiPercentDecimals={preferences.kpi_percent_decimals ?? 0}
          legend={[{ color: '#1d4ed8', label: 'OTIF %', dataKey: 'otif' }, { color: '#dc2626', label: 'Target', dataKey: 'target' }, { color: '#f97316', label: 'Trend', dataKey: 'otifTrend' }, { color: '#10b981', label: 'OTIF PO Lines', dataKey: 'otifCount' }, { color: '#fbbf24', label: 'Arrears PO Lines', dataKey: 'arrears' }, { color: '#ef4444', label: 'Failed OTIF PO Lines', dataKey: 'failed' }]}
          hoveredSeries={hoveredOtifSeries} onSeriesHover={setHoveredOtifSeries}>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={otifDataWithTrend} margin={milestoneMargin(otifMarks)} onClick={(chartData) => handleChartClick(chartData, 'otif')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} domain={otifDomain} allowDataOverflow={true} padding={{ top: 8 }} tickFormatter={(v) => `${v}%`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 'auto']} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} content={<OTIFChartTooltip />} />
              {milestoneLines(otifMarks, 'left')}
              <Bar yAxisId="right" dataKey="otifCount" stackId="a" fill="#10b981" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otifCount' ? 0.2 : 1} name="OTIF PO Lines" />
              <Bar yAxisId="right" dataKey="arrears" stackId="a" fill="#fbbf24" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'arrears' ? 0.2 : 1} name="Arrears PO Lines" />
              <Bar yAxisId="right" dataKey="failed" stackId="a" fill="#ef4444" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'failed' ? 0.2 : 1} name="Failed OTIF PO Lines" />
              <Line yAxisId="left" type="monotone" dataKey="otif" stroke="#1d4ed8" strokeWidth={hoveredOtifSeries === 'otif' ? 4 : 3} strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otif' ? 0.2 : 1} dot={{ r: hoveredOtifSeries === 'otif' ? 8 : 6, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTIF %" />
              <Line yAxisId="left" type="monotone" dataKey="target" stroke="#dc2626" strokeWidth={hoveredOtifSeries === 'target' ? 4 : 3} strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'target' ? 0.2 : 1} strokeDasharray="8 4" dot={false} name="Target" />
              <Line yAxisId="left" type="monotone" dataKey="otifTrend" stroke="#f97316" strokeWidth={hoveredOtifSeries === 'otifTrend' ? 3 : 2} strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otifTrend' ? 0.2 : 1} strokeDasharray="5 5" dot={false} name="Trend" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>
        </div>}
      </div>
      )}

      {/* Second Row Charts */}
      <div className={`grid gap-4 ${showOTD ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {/* Spend by Month */}
        <ChartWrapper title={`${portalLabels.spendByMonth} (${preferences.currency})`} subtitle={`${portalLabels.spendByMonthSubtitle} ${preferences.currency}`} data={spendDataWithTrend} exportFilename="spend-by-month"
          legend={[{ color: '#3b82f6', label: 'Spend', dataKey: 'spend' }, { color: '#f97316', label: 'Trend', dataKey: 'trend' }]}
          hoveredSeries={hoveredSpendSeries} onSeriesHover={setHoveredSpendSeries}>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={spendDataWithTrend} onClick={(chartData) => handleChartClick(chartData, 'spend')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              {/* CR-4537: interval={0} forces every month tick to render; angle + smaller font keep them readable when dense */}
              <XAxis dataKey="month" tick={{ fontSize: spendDataWithTrend.length > 24 ? 9 : 11 }} interval={0} angle={spendDataWithTrend.length > 12 ? -45 : 0} textAnchor={spendDataWithTrend.length > 12 ? 'end' : 'middle'} height={spendDataWithTrend.length > 12 ? 55 : 30} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={axisAmount} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={currencyTooltipFormatter} />
              <Area type="monotone" dataKey="spend" stroke="#3b82f6" fill="#93c5fd" fillOpacity={hoveredSpendSeries && hoveredSpendSeries !== 'spend' ? 0.2 : 0.8} strokeOpacity={hoveredSpendSeries && hoveredSpendSeries !== 'spend' ? 0.2 : 1} strokeWidth={hoveredSpendSeries === 'spend' ? 3 : 2} name="Spend" />
              <Line type="monotone" dataKey="trend" stroke="#f97316" strokeWidth={hoveredSpendSeries === 'trend' ? 3 : 2} strokeOpacity={hoveredSpendSeries && hoveredSpendSeries !== 'trend' ? 0.2 : 1} strokeDasharray="5 5" dot={false} name="Trend" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>

        {/* OTD Chart */}
        {showOTD && <ChartWrapper title="OTD" data={otdDataWithTrend} exportFilename="otd-trend" kpiPercentDecimals={preferences.kpi_percent_decimals ?? 0}
          legend={[{ color: '#1d4ed8', label: 'OTD %', dataKey: 'otd' }, { color: '#dc2626', label: 'Target', dataKey: 'target' }, { color: '#a855f7', label: 'Trend', dataKey: 'otdTrend' }, { color: '#10b981', label: 'On Time', dataKey: 'onTime' }, { color: '#fbbf24', label: 'Early', dataKey: 'early' }, { color: '#f97316', label: 'Late', dataKey: 'late' }]}
          hoveredSeries={hoveredOtdSeries} onSeriesHover={setHoveredOtdSeries}>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={otdDataWithTrend} margin={milestoneMargin(otdMarks)} onClick={(chartData) => handleChartClick(chartData, 'otd')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} domain={otdDomain} allowDataOverflow={true} padding={{ top: 8 }} tickFormatter={(v) => `${v}%`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              {/* CR-4672: Custom tooltip — reorders series and formats %s with 2dp */}
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} content={<OTDChartTooltip />} />
              {milestoneLines(otdMarks, 'left')}
              <Bar yAxisId="right" dataKey="onTime" stackId="a" fill="#10b981" fillOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'onTime' ? 0.2 : 1} name="On-Time" />
              <Bar yAxisId="right" dataKey="early" stackId="a" fill="#fbbf24" fillOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'early' ? 0.2 : 1} name="Early" />
              <Bar yAxisId="right" dataKey="late" stackId="a" fill="#f97316" fillOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'late' ? 0.2 : 1} name="Late" />
              <Line yAxisId="left" type="monotone" dataKey="otd" stroke="#1d4ed8" strokeWidth={hoveredOtdSeries === 'otd' ? 4 : 3} strokeOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'otd' ? 0.2 : 1} dot={{ r: hoveredOtdSeries === 'otd' ? 8 : 6, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTD %" />
              <Line yAxisId="left" type="monotone" dataKey="target" stroke="#dc2626" strokeWidth={hoveredOtdSeries === 'target' ? 4 : 3} strokeOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'target' ? 0.2 : 1} strokeDasharray="8 4" dot={false} name="Target" />
              <Line yAxisId="left" type="monotone" dataKey="otdTrend" stroke="#a855f7" strokeWidth={hoveredOtdSeries === 'otdTrend' ? 3 : 2} strokeOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'otdTrend' ? 0.2 : 1} strokeDasharray="5 5" dot={false} name="Trend" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>}
      </div>

      {/* Third Row - Arrears Backlog (weekly age buckets, drawn oldest first: 12+..1) */}
      <ChartWrapper title="Arrears Backlog (Weeks Overdue)" exportFilename="arrears-backlog"
        legend={[{ color: '#10b981', label: 'Low' }, { color: '#eab308', label: 'Medium' }, { color: '#ef4444', label: 'High' }]}>
        <ArrearsBacklogChart
          data={data.charts.arrearsBacklog?.map(d => ({ week: d.week, count: d.count || 0, value: d.value, date_from: d.date_from, date_to: d.date_to })) || []}
          onBarClick={handleArrearsBacklogClick}
          currency={preferences.currency}
        />
        {arrearsValueMissing > 0 && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="arrears-value-missing">
            {arrearsValueMissing.toLocaleString()} overdue line{arrearsValueMissing === 1 ? ' has' : 's have'} no value and {arrearsValueMissing === 1 ? 'is' : 'are'} counted but not valued.
          </p>
        )}
      </ChartWrapper>

      {/* Fourth Row Charts - Historical Arrears Split. W3-T14 (FIX-04): frozen
          month-end snapshots per month — Historical mode only; a month never
          captured is a gap, one between two captured months a zero (CR-8660). */}
      {filters?.dataMode !== 'live' && (
      <div className="grid grid-cols-2 gap-4">
        <ChartWrapper title={`Historical Arrears by Value (${preferences.currency})`} data={data.charts.historicalArrears} exportFilename="historical-arrears-value"
          legend={[{ color: '#ef4444', label: 'Arrears Value' }]}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.charts.historicalArrears} onClick={(chartData) => handleChartClick(chartData, 'arrears')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={axisAmount} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={currencyTooltipFormatter} />
              <Line type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Arrears Value" connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <ChartWrapper title="Historical Arrears (Count)" data={data.charts.historicalArrears?.map(d => ({ ...d, count: d.count ?? null }))} exportFilename="historical-arrears-count"
          legend={[{ color: '#f97316', label: 'PO Lines' }]}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.charts.historicalArrears?.map(d => ({ ...d, count: d.count ?? null }))} onClick={(chartData) => handleChartClick(chartData, 'arrears')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Line type="monotone" dataKey="count" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} name="PO Lines in Arrears" connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>
      )}

      {/* Fifth Row Charts */}
      <div className="grid grid-cols-2 gap-4">
        <ChartWrapper title="Live NCRs Overdue" data={data.charts.liveNCRsOverdue} exportFilename="live-ncrs-overdue"
          legend={[{ color: '#ef4444', label: 'Overdue' }, { color: '#fbbf24', label: 'Pending' }]}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.charts.liveNCRsOverdue} onClick={(chartData) => handleChartClick(chartData, 'ncrs')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="overdue" stackId="ncrs" fill="#ef4444" name="Overdue" onClick={liveNcrsBarClick('overdue', (m, seg) => handleChartClick({ activeLabel: m }, 'ncrs', seg))} />
              <Bar dataKey="pending" stackId="ncrs" fill="#fbbf24" name="Pending" onClick={liveNcrsBarClick('pending', (m, seg) => handleChartClick({ activeLabel: m }, 'ncrs', seg))} />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Performance Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 h-[200px] items-center">
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Total PO Lines</p>
                <p className="text-2xl font-bold">{data.purchasing.totalLines.toLocaleString()}</p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Total NCRs</p>
                <p className="text-2xl font-bold">{data.quality.ncrs.toLocaleString()}</p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Active Suppliers</p>
                <p className="text-2xl font-bold">{data.suppliers.active.toLocaleString()}</p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Open POs</p>
                <p className="text-2xl font-bold">{data.purchasing.openPOs.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">All orders</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schedule Collaboration (DE feature flag) */}
      {scheduleCollabEnabled && <ScheduleCollaborationSection />}

      {/* Modals */}
      {drillDownData && <DrillDownModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data={drillDownData} currency={currency.currentCurrency} />}
      <OTIFDrilldownModal isOpen={isOTIFModalOpen} onClose={() => setIsOTIFModalOpen(false)} filters={otifModalFilters} contextLabel={otifModalContext} initialTab={otifModalInitialTab} headline={otifModalHeadline} />
      <OTDDrilldownModal isOpen={isOTDModalOpen} onClose={() => setIsOTDModalOpen(false)} filters={otdModalFilters} contextLabel={otdModalContext} initialTab={otdModalInitialTab} headline={otdModalHeadline} />
    </div>
  )
}
